import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-hk';
import 'dayjs/locale/zh-tw';
import relativeTime from 'dayjs/plugin/relativeTime';
import { clone } from 'lodash';

dayjs.extend(relativeTime);

type NumberToDateOrElse<T> = T extends number ? Date : T;
type DateToNumberOrElse<T> = T extends Date ? number : T;
// interface RelativeTimeUnit {
//   str: string;
//   interval: [number, number];
//   withoutTime?: boolean;
// }

export const numberToDate = <T = unknown>(value: T): NumberToDateOrElse<T> => {
  if (typeof value === 'number') {
    return new Date(value) as NumberToDateOrElse<T>;
  }
  return value as NumberToDateOrElse<T>;
};

export const numberToCloneDate = <T = unknown>(
  value: T,
): NumberToDateOrElse<T> => {
  if (typeof value === 'number') {
    return new Date(value) as NumberToDateOrElse<T>;
  }
  if (value instanceof Date) {
    return clone(value) as NumberToDateOrElse<T>;
  }
  return value as NumberToDateOrElse<T>;
};

type Predicate = (s: string) => boolean;
/**
 * Deep transform timestamps of an object into date instances
 * @param  {Object} object
 * @return {Object}
 */
export const deepTransformTimestamp = <O extends object>(
  object: O,
  predicate: Predicate = (s) => s.endsWith('At'),
): O => {
  const recur = <T>(value: T, propName?: string): Date | T => {
    if (propName && predicate(propName)) {
      return numberToCloneDate(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => recur(item) as unknown) as T;
    }

    if (Object(value) === value) {
      return Object.entries<number | T[keyof T]>(
        value as { [K in keyof T]: number | T[K] },
      ).reduce(
        (acc, [key, val]) => {
          acc[key as keyof T] = recur(val, key) as Date | T[keyof T];
          return acc;
        },
        {} as { [K in keyof T]: Date | T[K] },
      ) as Date | T;
    }

    return value as NumberToDateOrElse<T>;
  };

  return recur(object) as O;
};

/**
 * Deep transform date properties of an object into timestamps
 * @param  {Object} object
 * @return {Object}
 */
export const deepTransformDate = <T>(value: T): DateToNumberOrElse<T> => {
  if (value instanceof Date) {
    return value.getTime() as DateToNumberOrElse<T>;
  }

  if (Array.isArray(value)) {
    return value.map(
      (item) => deepTransformDate(item) as unknown,
    ) as DateToNumberOrElse<T>;
  }

  // prevent the removal of property isUndefined
  if (Object(value) === value && value !== undefined) {
    return Object.entries<Date | T[keyof T]>(
      value as { [K in keyof T]: Date | T[K] },
    ).reduce(
      (acc, [key, val]) => {
        acc[key as keyof T] = deepTransformDate(val) as number | T[keyof T];
        return acc;
      },
      {} as { [K in keyof T]: number | T[K] },
    ) as DateToNumberOrElse<T>;
  }

  return value as DateToNumberOrElse<T>;
};

export const getTimeString = (
  ms: number,
  fmt = 'HH:mm:ss.SS',
  short = false,
) => {
  ms = Math.floor(ms);
  const hours = Math.floor(Math.floor(ms / 1000) / (60 * 60));

  const days = /(d+)/.test(fmt) ? Math.floor(hours / 24) : 0;

  const divisor_for_minutes = Math.floor(ms / 1000) % (60 * 60);
  const minutes = Math.floor(divisor_for_minutes / 60);

  const divisor_for_seconds = divisor_for_minutes % 60;
  const seconds = Math.ceil(divisor_for_seconds);

  const milliseconds = ms % 1000;

  const o = {
    'D+': days,
    HH: hours - days * 24, // 小時
    H: hours - days * 24, // 小時
    mm: minutes, // 分
    m: minutes, // 分
    ss: seconds, // 秒
    s: seconds, // 秒
    SSS: milliseconds, // 毫秒
    SS: Math.floor(milliseconds / 10), // 毫秒
    S: Math.floor(milliseconds / 100), // 毫秒
  };

  Object.entries(o).forEach(([k, v]) => {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length === 1
          ? String(v)
          : String(v).padStart(RegExp.$1.length, '0'),
      );
    }
  });

  if (short) {
    return finalCheckCustomTimeFormat(fmt) ?? '';
  }
  return fmt;
};
export const getDateString = ({
  date: _date,
  ms = Date.now(),
  fmt = 'YYYY-MM-DD HH:mm:ss.SSS',
  lang,
  normal = false,
  remain = '',
  short = false,
}: {
  date?: Date;
  ms?: number;
  fmt?: string;
  lang?: string;
  normal?: boolean;
  remain?: string;
  short?: boolean;
}) => {
  const date = dayjs(_date ?? ms);
  if (normal) {
    return date.format(fmt);
  } else {
    if (lang) {
      const result = new Intl.DateTimeFormat(lang, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(_date ?? new Date(ms));
      return result + (remain ? date.format(remain) : '');
    } else {
      let result = date.format(fmt);
      if (short) {
        result = finalCheckCustomTimeFormat(result) ?? result;
      }
      return result;
    }
  }
};

/**
 * e.g 2020-10-20 00:00:10.000 => 10.00
 * More in test cases
 */
export const finalCheckCustomTimeFormat = (
  inputString: string,
): string | undefined => {
  return inputString.match(
    /(?:0*:?)*((?:[1-9][0-9]?(?::[0-9]{2}){0,2}|0)(?:\.[0-9]{1,2})?)[0-9]?$/,
  )?.[1];
};

const dayjsLocaleMap: Record<string, string> = {
  jp: 'ja',
  zh: 'zh-hk',
};

const justNowTranslations: Record<string, string> = {
  en: 'Just now',
  ja: 'ちょうど今',
  'zh-CN': '刚刚',
  'zh-hk': '剛剛',
  'zh-TW': '剛剛',
};

export const timeAgo = (
  date: Date | number | string,
  locale: string = 'en',
): string => {
  // convert the locale if necessary
  const dayjsLocale = dayjsLocaleMap[locale] ?? locale;

  const now = dayjs();
  const targetDate = dayjs(date);
  const diff = now.diff(targetDate, 'seconds');

  if (diff < 60) {
    return justNowTranslations[dayjsLocale] ?? 'Just now';
  } else {
    return targetDate.locale(dayjsLocale).fromNow();
  }
};
